import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';

const StaffPollResults = () => {
    const { id: initialId } = useParams();
    const navigate = useNavigate();
    const [id, setId] = useState(initialId);
    const [poll, setPoll] = useState(null);
    const [stats, setStats] = useState(null);
    const [allPolls, setAllPolls] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showSelector, setShowSelector] = useState(false);

    const COLORS = ['#9d4edd', '#4895ef', '#4cc9f0', '#f72585', '#7209b7', '#3f37c9'];

    useEffect(() => {
        fetchAllPolls();
    }, []);

    useEffect(() => {
        if (id) {
            fetchPollAndStats(id);
            const interval = setInterval(() => fetchStatsOnly(id), 2000);
            return () => clearInterval(interval);
        }
    }, [id]);

    const fetchAllPolls = async () => {
        try {
            const res = await api.get('/polls/all');
            setAllPolls(res.data);
            if ((!id || id === '0') && res.data.length > 0) {
                const active = res.data.find(p => p.status === 'active') || res.data[0];
                setId(active.id);
            }
        } catch (error) {
            console.error("Error fetching all polls", error);
        }
    };

    const fetchPollAndStats = async (pollId) => {
        try {
            const pollRes = await api.get(`/polls/${pollId}`);
            setPoll(pollRes.data);
            const statsRes = await api.get(`/polls/${pollId}/stats`);
            setStats(statsRes.data);
        } catch (error) {
            console.error("Error fetching poll data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStatsOnly = async (pollId) => {
        try {
            const statsRes = await api.get(`/polls/${pollId}/stats`);
            setStats(statsRes.data);
            const pollRes = await api.get(`/polls/${pollId}`);
            setPoll(pollRes.data);
        } catch (error) {
            console.error("Error syncing stats", error);
        }
    };

    const toggleStatus = async (newStatus) => {
        try {
            await api.put(`/polls/${id}/status`, { status: newStatus });
            fetchPollAndStats(id);
            fetchAllPolls();
        } catch (error) {
            console.error("Error toggling status", error);
        }
    };

    const selectPoll = (newId) => {
        setId(newId);
        setShowSelector(false);
        navigate(`/interactiva-stats/${newId}`);
    };

    if (isLoading && !id) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-white/10 border-t-purple-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!poll || !stats) {
        return (
            <div className="p-10 text-center text-white">
                <p className="text-white font-black uppercase tracking-widest">No hay encuestas disponibles.</p>
                <button onClick={() => navigate('/interactiva-gestion')} className="mt-4 text-purple-400 font-bold uppercase tracking-widest">Ir a Gestión</button>
            </div>
        );
    }

    const hasVotes = stats.totalVotes > 0;

    const getPieSlices = () => {
        let cumulativePercent = 0;
        return stats.results.map((res, i) => {
            const percent = stats.totalVotes > 0 ? (res.votes / stats.totalVotes) : 0;
            const startX = Math.cos(2 * Math.PI * cumulativePercent);
            const startY = Math.sin(2 * Math.PI * cumulativePercent);
            cumulativePercent += percent;
            const endX = Math.cos(2 * Math.PI * cumulativePercent);
            const endY = Math.sin(2 * Math.PI * cumulativePercent);
            const largeArcFlag = percent > 0.5 ? 1 : 0;
            const pathData = [`M ${startX} ${startY}`, `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`, `L 0 0`].join(' ');
            return { path: pathData, color: COLORS[i % COLORS.length] };
        });
    };

    return (
        <div className="animate-fadeIn space-y-4 pb-20">
            {/* Minimal Header to save vertical space */}
            <header className="flex justify-between items-center bg-white/10 p-4 rounded-3xl border border-white/20 backdrop-blur-3xl shadow-xl gap-4 relative z-[110]">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/interactiva-gestion')} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all border border-white/10">
                        <span className="text-white text-lg">←</span>
                    </button>
                    <h1 className="text-xl font-black text-white uppercase tracking-tighter italic">En Vivo</h1>
                </div>
                
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <button 
                            onClick={() => setShowSelector(!showSelector)}
                            className="bg-white text-[#1c0042] border border-white px-5 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-white/90 transition-all shadow-md flex items-center gap-2"
                        >
                            <span>Trivia</span>
                            <span className="text-[8px] opacity-40">{showSelector ? '▲' : '▼'}</span>
                        </button>
                        
                        {showSelector && (
                            <div className="absolute top-full right-0 mt-2 w-[320px] bg-[#2a005c] border border-white/30 rounded-[25px] shadow-2xl overflow-hidden z-[150] animate-slideUp">
                                <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1">
                                    {allPolls.map(p => (
                                        <button 
                                            key={p.id}
                                            onClick={() => selectPoll(p.id)}
                                            className={`w-full text-left p-3 rounded-xl transition-all mb-1 ${p.id === parseInt(id) ? 'bg-purple-600 text-white' : 'text-white hover:bg-white/10'}`}
                                        >
                                            <p className="text-[11px] font-black uppercase italic tracking-tight line-clamp-1">{p.question}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {poll.status === 'active' ? (
                        <button onClick={() => toggleStatus('closed')} className="bg-red-600 text-white px-5 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest">Cerrar</button>
                    ) : (
                        <button onClick={() => toggleStatus('active')} className="bg-emerald-600 text-white px-5 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest">Publicar</button>
                    )}
                </div>
            </header>

            <section className="bg-white/10 backdrop-blur-3xl rounded-[40px] border border-white/20 p-8 shadow-2xl relative overflow-hidden">
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-8 items-start">
                    
                    {/* Left: Question and Main Chart (Col 1-7) */}
                    <div className="xl:col-span-7 space-y-6">
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 bg-purple-600 px-4 py-1 rounded-full shadow-md">
                                <div className={`w-2 h-2 rounded-full ${poll.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}></div>
                                <span className="text-[9px] font-black text-white uppercase tracking-widest">
                                    {poll.status === 'active' ? 'Activa' : 'Cerrada'}
                                </span>
                            </div>
                            <h2 className="text-4xl font-black text-white leading-tight italic uppercase tracking-tighter drop-shadow-md">
                                {poll.question}
                            </h2>
                        </div>

                        <div className="bg-black/20 p-8 rounded-[40px] border border-white/10 h-[420px] flex items-center justify-between relative shadow-inner">
                            {hasVotes ? (
                                <>
                                    <div className="relative w-72 h-72">
                                        <svg viewBox="-1.1 -1.1 2.2 2.2" className="transform -rotate-90 w-full h-full filter drop-shadow-2xl">
                                            {getPieSlices().map((slice, i) => (
                                                <path key={i} d={slice.path} fill={slice.color} className="transition-all duration-1000" />
                                            ))}
                                            <circle cx="0" cy="0" r="0.75" fill="#1c0042" />
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                            <p className="text-6xl font-black text-white leading-none tracking-tighter">{stats.totalVotes}</p>
                                            <p className="text-[10px] font-black text-white uppercase tracking-[0.4em] mt-2 opacity-60">Votos</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-4 bg-white/5 p-6 rounded-3xl border border-white/10 flex-grow ml-8 max-w-[200px]">
                                        {stats.results.map((res, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                                                <span className="text-[10px] font-black text-white uppercase tracking-tight line-clamp-2">{res.option_text}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="text-center w-full space-y-4">
                                    <span className="text-6xl opacity-30 animate-bounce">📊</span>
                                    <p className="text-white font-black text-xs uppercase tracking-[0.5em] italic">Esperando Interacción</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Numbers and List (Col 8-12) */}
                    <div className="xl:col-span-5 space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 p-6 rounded-[30px] border border-white/30 shadow-xl flex flex-col items-center justify-center">
                                <p className="text-white/70 font-black text-[9px] uppercase tracking-widest mb-1">Participación</p>
                                <p className="text-5xl font-black text-white italic">
                                    {stats.totalUsers > 0 ? Math.round((stats.totalVotes / stats.totalUsers) * 100) : 0}%
                                </p>
                            </div>
                            <div className="bg-white/10 p-6 rounded-[30px] border border-white/20 shadow-xl flex flex-col items-center justify-center">
                                <p className="text-white/50 font-black text-[9px] uppercase tracking-widest mb-1">Total Usuarios</p>
                                <p className="text-5xl font-black text-white italic">{stats.totalUsers}</p>
                            </div>
                        </div>

                        <div className="bg-black/30 p-6 rounded-[40px] border border-white/10 shadow-inner max-h-[400px] overflow-y-auto custom-scrollbar">
                            <h3 className="text-[11px] font-black text-white/40 uppercase tracking-[0.4em] mb-6 text-center">Análisis de Opciones</h3>
                            <div className="space-y-5">
                                {stats.results.map((res, idx) => {
                                    const percent = stats.totalVotes > 0 ? Math.round((res.votes / stats.totalVotes) * 100) : 0;
                                    return (
                                        <div key={res.id} className="space-y-2">
                                            <div className="flex justify-between items-end px-2">
                                                <span className="text-[12px] font-black text-white uppercase italic tracking-tight flex items-center gap-3">
                                                    <div className="w-2.5 h-2.5 rounded-full shadow-md" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                                                    <span className="line-clamp-1">{res.option_text}</span>
                                                </span>
                                                <div className="flex items-baseline gap-2">
                                                    <span className="text-xl font-black text-white italic">{percent}%</span>
                                                </div>
                                            </div>
                                            <div className="w-full h-4 bg-black/40 rounded-full overflow-hidden border border-white/10 p-1">
                                                <div 
                                                    className="h-full rounded-full transition-all duration-1000 shadow-lg relative overflow-hidden"
                                                    style={{ width: `${percent}%`, backgroundColor: COLORS[idx % COLORS.length] }}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent"></div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </div>
    );
};

export default StaffPollResults;
